import { useEffect, useState } from "react";
import { api, apiError } from "./api";
import { Card, PageHead, Empty } from "./widgets";
import { toast } from "sonner";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7 Days" },
  { key: "30d", label: "30 Days" },
  { key: "90d", label: "90 Days" },
];

const Bar = ({ label, value, max }) => (
  <div className="flex items-center gap-3">
    <span className="w-24 truncate text-xs text-[#171717]/60 sm:w-40">{label || "/"}</span>
    <div className="h-4 flex-1 rounded-[2px] bg-[#171717]/6">
      <div className="h-full rounded-[2px] bg-[#C6A46A]" style={{ width: `${max ? Math.max(3, (value / max) * 100) : 0}%` }} />
    </div>
    <span className="w-8 shrink-0 text-right text-xs text-[#171717]/70">{value}</span>
  </div>
);

const Analytics = () => {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/admin/analytics?range=${range}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [range]);

  return (
    <div data-testid="analytics-page">
      <PageHead
        title="Analytics"
        sub="First-party, privacy-first metrics collected by this website. No third-party trackers."
        actions={
          <div className="flex flex-wrap gap-2">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                data-testid={`range-${r.key}`}
                className={`min-h-[44px] rounded-[3px] border px-3.5 py-2 text-[0.62rem] font-medium uppercase tracking-[0.14em] transition-colors ${
                  range === r.key ? "border-[#171717] bg-[#171717] text-[#C6A46A]" : "border-[#171717]/15 text-[#171717]/60 hover:border-[#C6A46A]"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        }
      />
      {!data ? (
        <p className="text-sm text-[#171717]/50">Loading…</p>
      ) : data.uniqueVisitors === 0 && data.pageViews === 0 ? (
        <Empty>No traffic recorded in this range yet. Metrics appear automatically as visitors use the site.</Empty>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[
              ["Unique Visitors", data.uniqueVisitors],
              ["Page Views", data.pageViews],
              ["Consultation Requests", data.consultationRequests],
              ["Conversion Rate", `${data.conversionRate}%`],
              ["CTA Clicks", data.ctaClicks],
              ["Phone Clicks", data.phoneClicks],
              ["Form Submissions", data.formSubmissions],
              ["Leads Created", data.leads],
            ].map(([label, value]) => (
              <Card key={label}>
                <p className="text-[0.6rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">{label}</p>
                <p className="mt-3 text-3xl font-light text-[#171717]">{value}</p>
              </Card>
            ))}
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <Card>
              <p className="mb-4 text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">Most viewed pages</p>
              <div className="space-y-2.5">
                {data.topPages.length === 0 && <p className="text-xs text-[#171717]/45">No data yet.</p>}
                {data.topPages.map(([p, v]) => <Bar key={p} label={p} value={v} max={data.topPages[0]?.[1]} />)}
              </div>
            </Card>
            <Card>
              <p className="mb-4 text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">Most read articles</p>
              <div className="space-y-2.5">
                {data.topArticles.length === 0 && <p className="text-xs text-[#171717]/45">No data yet.</p>}
                {data.topArticles.map(([p, v]) => <Bar key={p} label={p.replace(/^\/es/, "").replace("/blog/", "")} value={v} max={data.topArticles[0]?.[1]} />)}
              </div>
            </Card>
            <Card>
              <p className="mb-4 text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">Languages & devices</p>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2.5">
                  {Object.entries(data.languages).map(([k, v]) => <Bar key={k} label={k.toUpperCase()} value={v} max={Math.max(...Object.values(data.languages), 1)} />)}
                  {Object.keys(data.languages).length === 0 && <p className="text-xs text-[#171717]/45">No data yet.</p>}
                </div>
                <div className="space-y-2.5">
                  {Object.entries(data.devices).map(([k, v]) => <Bar key={k} label={k} value={v} max={Math.max(...Object.values(data.devices), 1)} />)}
                  {Object.keys(data.devices).length === 0 && <p className="text-xs text-[#171717]/45">No data yet.</p>}
                </div>
              </div>
            </Card>
            <Card>
              <p className="mb-4 text-[0.62rem] font-medium uppercase tracking-[0.16em] text-[#171717]/50">Most requested services</p>
              <div className="space-y-2.5">
                {data.topServices.length === 0 && <p className="text-xs text-[#171717]/45">No data yet.</p>}
                {data.topServices.map(([s, v]) => <Bar key={s} label={s} value={v} max={data.topServices[0]?.[1]} />)}
              </div>
            </Card>
          </div>
          <p className="mt-6 text-[0.65rem] text-[#171717]/40">
            Anonymous visitors are counted as sessions only — this system never identifies visitors by name unless they submit a form.
          </p>
        </>
      )}
    </div>
  );
};

export default Analytics;
